/**
 * Bar marks for the editor preview.
 *
 * Mirrors Chart\BarRenderer. Bars are HTML elements, not SVG: only HTML gives a
 * true 4px corner radius, an exact 2px gap between neighbours and a real
 * `max-width` cap on thickness.
 */

import { baselineFraction, valueFraction } from './geometry';
import { formatValue } from './format';

/** Share of a category band the marks may occupy; the rest is air. */
const BAND_FILL = 0.82;

export function BarSlots( { config, index } ) {
	const { scale } = config;
	const baseline = baselineFraction( scale );
	const pad = ( ( 1 - BAND_FILL ) / 2 ) * 100;
	const slots = config.stacked ? 1 : Math.max( 1, config.series.length );
	const width = ( BAND_FILL * 100 ) / slots;

	// Positive and negative parts of a stack grow away from zero separately.
	let up = 0;
	let down = 0;

	const bars = config.series.map( ( series, slot ) => {
		const value = series.data[ index ] ?? 0;
		let start;
		let size;

		if ( config.stacked ) {
			const from = value < 0 ? down : up;
			const to = from + value;
			if ( value < 0 ) {
				down = to;
			} else {
				up = to;
			}
			start = valueFraction( Math.min( from, to ), scale );
			size = Math.abs(
				valueFraction( to, scale ) - valueFraction( from, scale )
			);
		} else {
			const fraction = valueFraction( value, scale );
			start = Math.min( baseline, fraction );
			size = Math.abs( fraction - baseline );
		}

		const bar = (
			<span
				key={ slot }
				className={ `brandy-chart__bar${
					value < 0 ? ' brandy-chart__bar--negative' : ''
				}` }
				style={ {
					'--bar-start': `${ start * 100 }%`,
					'--bar-size': `${ size * 100 }%`,
					'--bar-color': series.color,
				} }
			/>
		);

		if ( config.stacked ) {
			return bar;
		}

		return (
			<span
				key={ slot }
				className="brandy-chart__slot"
				style={ {
					'--slot-start': `${ pad + slot * width }%`,
					'--slot-size': `${ width }%`,
				} }
			>
				{ bar }
				<BarLabel
					config={ config }
					index={ index }
					slot={ slot }
					value={ value }
				/>
			</span>
		);
	} );

	if ( ! config.stacked ) {
		return bars;
	}

	const total = config.series.reduce(
		( sum, series ) => sum + ( series.data[ index ] ?? 0 ),
		0
	);

	return (
		<span
			className="brandy-chart__slot"
			style={ {
				'--slot-start': `${ pad }%`,
				'--slot-size': `${ BAND_FILL * 100 }%`,
			} }
		>
			{ bars }
			{ config.valueLabels !== 'none' && (
				// An interior segment has no free end to label without
				// clipping, so a stack labels its total at the outer end.
				<span
					className="brandy-chart__bar-label"
					style={ {
						'--label-pos': `${
							valueFraction( total, scale ) * 100
						}%`,
					} }
				>
					{ formatValue( total, config.format ) }
				</span>
			) }
		</span>
	);
}

/**
 * The value label for one bar, when it earns one.
 *
 * `selective` labels each series' extreme — the bar the chart is read for —
 * rather than every bar, which goes unread.
 */
function BarLabel( { config, index, slot, value } ) {
	if ( config.valueLabels === 'none' ) {
		return null;
	}

	if ( config.valueLabels === 'selective' ) {
		const data = config.series[ slot ].data;
		if ( ! data.length || data.indexOf( Math.max( ...data ) ) !== index ) {
			return null;
		}
	}

	return (
		<span
			className="brandy-chart__bar-label"
			style={ {
				// Anchored to the value end, which for a bar below zero is the
				// bottom of the bar rather than start + size.
				'--label-pos': `${ valueFraction( value, config.scale ) * 100 }%`,
			} }
		>
			{ formatValue( value, config.format ) }
		</span>
	);
}
